import { useTranslation } from "react-i18next";
import { Plane, Hotel, Camera } from "lucide-react";

/**
 * Phase 14.9.9 — Cost summary card.
 *
 * Three rows (flight / hotel / activities) + total + disclaimer. Each
 * row can be in two states: priced (`amount` is a number) or
 * `pricing_unavailable` (renders the label only, no CTA). The total
 * reflects only successfully-priced components — never blocks render.
 *
 * Design: matches the existing B&W card aesthetic; orange dot top-
 * right (category=itinerary). High-contrast "Book" links per row
 * when a tracked `book_url` is present.
 */

function fmtMoney(amount, currency) {
    if (amount === null || amount === undefined) return null;
    const cur = (currency || "USD").toUpperCase();
    const symbol = (
        cur === "INR" ? "₹"
        : cur === "USD" ? "$"
        : cur === "EUR" ? "€"
        : cur === "GBP" ? "£"
        : `${cur} `
    );
    try {
        return `${symbol}${Number(amount).toLocaleString("en-IN")}`;
    } catch (e) {
        return `${symbol}${amount}`;
    }
}

function Row({ icon: Icon, comp, currency, testId, items }) {
    const unavailable = !comp || comp.pricing_unavailable
        || comp.amount === null || comp.amount === undefined;
    const amount = unavailable ? null : fmtMoney(comp.amount, comp.currency || currency);
    return (
        <div
            className="flex items-start justify-between gap-3 border-b border-[#F0F0F0] py-3 last:border-b-0"
            data-testid={testId}
        >
            <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5]">
                    <Icon size={14} strokeWidth={1.8} className="text-[#0A0A0A]" />
                </span>
                <div className="min-w-0">
                    <div className="text-[13.5px] font-medium text-[#0A0A0A]">
                        {comp?.label || "—"}
                    </div>
                    {comp?.carrier_hint && !unavailable && (
                        <div className="mt-0.5 text-[11.5px] text-[#737373]">
                            {comp.carrier_hint}
                        </div>
                    )}
                    {comp?.indicative && (
                        <div className="mt-0.5 text-[11px] text-[#A3A3A3]">
                            Indicative — nearby-date snapshot
                        </div>
                    )}
                    {items && items.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-[11.5px] text-[#525252]">
                            {items.map((it) => (
                                <li key={it.name}>
                                    {it.name}
                                    {it.price != null && (
                                        <span className="ml-1 text-[#A3A3A3]">
                                            · {fmtMoney(it.price, it.currency || currency)}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
            <div className="shrink-0 text-right">
                {unavailable ? (
                    <span className="text-[12px] text-[#A3A3A3]">Unavailable</span>
                ) : (
                    <>
                        <div className="text-[16px] font-semibold tracking-tight text-[#0A0A0A]">
                            {amount}
                        </div>
                        {comp.book_url && (
                            <a
                                href={comp.book_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-block text-[11.5px] font-medium text-[#0A0A0A] underline underline-offset-2 hover:text-[#525252]"
                            >
                                Book
                            </a>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default function CostSummaryCard({ card }) {
    const { t } = useTranslation();
    const c = card.components || {};
    const currency = card.currency || "USD";
    const total = card.total || {};
    const totalLabel = total.amount != null
        ? fmtMoney(total.amount, total.currency || currency)
        : null;

    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="cost-summary-card"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full bg-[#F97316] ring-4 ring-[#F97316]/10"
            />
            <div className="mb-3 flex items-center gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-[#737373]">
                    Approx total
                </span>
                {card.indicative_date && (
                    <span
                        className="inline-flex items-center gap-1 rounded-full border border-[#E5E5E5] bg-white px-2 py-0.5 text-[10.5px] font-medium text-[#737373]"
                        data-testid="cost-indicative-pill"
                    >
                        Tentative dates
                    </span>
                )}
            </div>
            <div className="text-[13px] text-[#525252]">
                {card.duration_days
                    ? `${card.duration_days} days · ${card.duration_nights} nights · per person`
                    : "Per person"}
            </div>

            <div className="mt-4 rounded-xl border border-[#F0F0F0] bg-white">
                <div className="px-4">
                    <Row
                        icon={Plane}
                        comp={c.flight}
                        currency={currency}
                        testId="cost-row-flight"
                    />
                    <Row
                        icon={Hotel}
                        comp={c.hotel}
                        currency={currency}
                        testId="cost-row-hotel"
                    />
                    <Row
                        icon={Camera}
                        comp={c.activities}
                        currency={currency}
                        testId="cost-row-activities"
                        items={c.activities?.items}
                    />
                </div>
                <div
                    className="flex items-end justify-between rounded-b-xl bg-[#0A0A0A] px-4 py-3 text-white"
                    data-testid="cost-total-row"
                >
                    <div className="text-[12px] uppercase tracking-wider text-[#A3A3A3]">
                        {total.label || "Approx total"}
                    </div>
                    <div className="text-[20px] font-semibold tracking-tight">
                        {totalLabel || "—"}
                    </div>
                </div>
            </div>

            <p className="mt-3 text-[11.5px] text-[#A3A3A3]" data-testid="cost-disclaimer">
                {card.disclaimer || "Approximate — final price varies with bookings."}
            </p>
        </article>
    );
}
